import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CloseIcon from "@material-ui/icons/Close";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const pairKey = (a, b) => `${a}-${b}`;

const scoreLabel = (score) => {
	if (score >= 0.95) return { label: "Muito provável", color: "#c62828", bg: "#fdecea" };
	if (score >= 0.75) return { label: "Provável", color: "#e65100", bg: "#fff3e0" };
	return { label: "Verificar", color: "#616161", bg: "#f0f0f0" };
};

// Pedido do usuario 2026-10-19: candidato sugerido so' por horario de
// atendimento (sem nome/numero parecido) precisa de um motivo visivel -
// senao o admin nao entende por que dois contatos sem nada em comum
// estao sendo sugeridos como duplicata.
const matchedByLabel = (matchedBy) => {
	if (matchedBy === "numero") return "número parecido";
	if (matchedBy === "horario de atendimento") return "mensagens próximas no horário";
	return "nome parecido";
};

// Sugestao automatica de contatos possivelmente duplicados - antes so'
// dava pra perceber isso durante um atendimento ao vivo (foi assim que
// quase se perdeu uma venda, caso real "Aparecida"/CLB 2026-08-29).
// Backend: FindDuplicateContactCandidatesService (pg_trgm no nome +
// numero batendo nos ultimos 8 digitos). "Ignorar" e' so' de sessao
// (nao persiste) - se incomodar toda vez, dá pra guardar depois.
const DuplicateContactsModal = ({ open, onClose, onCountChange }) => {
	const [candidates, setCandidates] = useState([]);
	const [loading, setLoading] = useState(false);
	const [confirmPair, setConfirmPair] = useState(null);
	const [useNumberFromB, setUseNumberFromB] = useState(false);
	const [merging, setMerging] = useState(false);

	const fetchCandidates = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await api.get("/contact-duplicate-candidates");
			setCandidates(data.candidates || []);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (open) {
			fetchCandidates();
		}
	}, [open, fetchCandidates]);

	// Avisa o botao que abre este modal (fora da arvore do Dialog) quantos
	// candidatos existem agora, pra mostrar um badge sem precisar abrir o
	// modal pra descobrir - antes disso a lista so era vista por quem
	// lembrava de clicar em "Possíveis Duplicados".
	useEffect(() => {
		onCountChange?.(candidates.length);
	}, [candidates, onCountChange]);

	const handleDismiss = async (c) => {
		try {
			await api.post("/contact-duplicate-candidates/dismiss", {
				contactAId: c.contactAId,
				contactBId: c.contactBId,
			});
			setCandidates((prev) =>
				prev.filter(
					(x) => !(x.contactAId === c.contactAId && x.contactBId === c.contactBId)
				)
			);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenMerge = (c) => {
		setConfirmPair(c);
		setUseNumberFromB(false);
	};

	const handleConfirmMerge = async () => {
		if (!confirmPair) return;
		setMerging(true);
		try {
			await api.put(`/contacts/${confirmPair.contactAId}/merge-with/${confirmPair.contactBId}`, {
				useNumberFromMerge: useNumberFromB,
			});
			toast.success("Contatos mesclados!");
			setConfirmPair(null);
			setCandidates((prev) =>
				prev.filter(
					(c) =>
						c.contactAId !== confirmPair.contactAId &&
						c.contactBId !== confirmPair.contactAId &&
						c.contactAId !== confirmPair.contactBId &&
						c.contactBId !== confirmPair.contactBId
				)
			);
		} catch (err) {
			toastError(err);
		} finally {
			setMerging(false);
		}
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
				<DialogTitle>
					Possíveis contatos duplicados
					<IconButton
						onClick={onClose}
						style={{ position: "absolute", right: 8, top: 8 }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					{loading && (
						<div style={{ textAlign: "center", padding: 24 }}>
							<CircularProgress size={28} />
						</div>
					)}
					{!loading && candidates.length === 0 && (
						<Typography variant="body2" color="textSecondary" style={{ padding: 16 }}>
							Nenhum candidato encontrado.
						</Typography>
					)}
					{!loading && candidates.length > 0 && (
						<List dense>
							{candidates.map((c) => {
								const style = scoreLabel(c.score);
								return (
									<ListItem
										key={pairKey(c.contactAId, c.contactBId)}
										divider
										style={{ flexWrap: "wrap" }}
									>
										<ListItemText
											primary={
												<span>
													{c.contactAName} <CompareArrowsIcon
														fontSize="inherit"
														style={{ verticalAlign: "middle", margin: "0 4px" }}
													/> {c.contactBName}
												</span>
											}
											secondary={`${c.contactANumber}  ·  ${c.contactBNumber}  ·  ${matchedByLabel(c.matchedBy)}`}
										/>
										<Chip
											size="small"
											label={style.label}
											style={{ color: style.color, backgroundColor: style.bg, fontWeight: "bold", marginRight: 8 }}
										/>
										<Button size="small" onClick={() => handleDismiss(c)}>
											Ignorar
										</Button>
										<Button size="small" color="secondary" onClick={() => handleOpenMerge(c)}>
											Mesclar
										</Button>
									</ListItem>
								);
							})}
						</List>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose}>Fechar</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={!!confirmPair} onClose={() => setConfirmPair(null)} maxWidth="sm" fullWidth>
				<DialogTitle>Mesclar contatos</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" gutterBottom>
						Todo o histórico de <b>{confirmPair?.contactBName}</b> (tickets, mensagens, notas,
						agendamentos, compromissos e tags) vai passar para <b>{confirmPair?.contactAName}</b>,
						e o registro de <b>{confirmPair?.contactBName}</b> será apagado. Essa ação não pode ser
						desfeita.
					</Typography>
					<FormControlLabel
						control={
							<Checkbox
								checked={useNumberFromB}
								onChange={(e) => setUseNumberFromB(e.target.checked)}
							/>
						}
						label={`Usar o número de ${confirmPair?.contactBName} (${confirmPair?.contactBNumber}) neste contato`}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmPair(null)} disabled={merging}>
						Cancelar
					</Button>
					<Button color="secondary" variant="contained" onClick={handleConfirmMerge} disabled={merging}>
						{merging ? "Mesclando..." : "Mesclar"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default DuplicateContactsModal;
